import { type FC, useEffect, useState } from "react";
import {
  useLocation, useNavigate, useParams, NavigationType, useNavigationType
} from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcImage from "shared/NcImage/NcImage";
import { MainTemplate } from "templates/MainTemplate";
import favicon from "../../images/logos/favicon_virotrip-new.png";
import {
  fetchBookingDone,
  type Root as RootBooking,
} from "services/fetch-bookings-done";
import toast from "react-hot-toast";
import { Formatter } from "utils/formatters";


export interface PayPageProps {
  className?: string;
}

export const BookingDone: FC<PayPageProps> = ({ className = "" }) => {
  const [bookingDetails, setBookingDetails] = useState<any>();
  const [isLoaded, setIsLoaded] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation()
  const booking: RootBooking = location?.state?.dataBooking

  useEffect(() => {
    if (!booking || navigationType === NavigationType.Pop) {
      return navigate("/home");
    }
  }, []);

  useEffect(() => {
    const fetch = async () => {
      if (!id) {
        // Se não houver detalhes do booking, redirecione para a página Home
        return navigate("/home");
      }

      if (booking?.not_update_recurrent_card) {
        toast.error("Houve um problema apenas ao alterar o cartão de crédito, por favor,tente alterar novamente em outro momento.", );
      }

      try {
        const booking = await fetchBookingDone(id);
        setBookingDetails(booking);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error("Erro ao carregar a reserva");
        }
        setBookingDetails(null);
      } finally {
        setIsLoaded(true);
      }
    };

    fetch();
  }, [id]);

  if (!isLoaded) {
    return (
      <MainTemplate>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-neutral-300"></div>
        </div>
      </MainTemplate>
    );
  }

  if (!bookingDetails) {
    return (
      <MainTemplate>
        <div className="flex flex-col items-center justify-center h-screen">
          <h1 className="text-2xl text-neutral-900 dark:text-neutral-100 mb-9">
            Não foi possível carregar os detalhes da reserva. Por favor, tente
            novamente mais tarde.
          </h1>

          <ButtonPrimary
            className="bg-[--blue] hover:bg-[--blue-dark] mt-8"
            href="/home"
          >
            Voltar para a página inicial
          </ButtonPrimary>
        </div>
      </MainTemplate>
    );
  }

  const renderContent = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
        <div>
          <h2 className="text-3xl lg:text-4xl font-semibold">
            Reserva efetuada com sucesso 🎉
          </h2>
        </div>

        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>

        <div className="space-y-6">
          <h3 className="text-2xl font-semibold">{`Sua reserva no ${booking?.hotel_name}`}</h3>
          <div className="flex flex-col sm:flex-row sm:items-center">
            <div className="flex-shrink-0 w-full sm:w-40">
              <div className=" aspect-w-4 aspect-h-3 sm:aspect-h-4 rounded-2xl overflow-hidden">
                <NcImage src={favicon} />
              </div>
            </div>
            <div className="pt-5  sm:pb-5 sm:px-5 space-y-3">
              <div>
                <span className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-1">
                  {booking?.city}
                </span>
                <span className="text-base sm:text-lg font-medium mt-1 block">
                  {booking?.room_description}
                </span>
              </div>

              <div className="w-10 border-b border-neutral-200  dark:border-neutral-700"></div>
            </div>
          </div>
          <div className="mt-6 border border-neutral-200 dark:border-neutral-700 rounded-3xl flex flex-col sm:flex-row divide-y sm:divide-x sm:divide-y-0 divide-neutral-200 dark:divide-neutral-700">
            <div className="flex-1 p-5 flex space-x-4">
              <svg
                className="w-8 h-8 text-neutral-300 dark:text-neutral-6000"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.33333 8.16667V3.5M18.6667 8.16667V3.5M8.16667 12.8333H19.8333M5.83333 24.5H22.1667C23.4553 24.5 24.5 23.4553 24.5 22.1667V8.16667C24.5 6.878 23.4553 5.83333 22.1667 5.83333H5.83333C4.54467 5.83333 3.5 6.878 3.5 8.16667V22.1667C3.5 23.4553 4.54467 24.5 5.83333 24.5Z"
                  stroke="#D1D5DB"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <div className="flex flex-col">
                <span className="text-sm text-neutral-400">Data</span>
                <span className="mt-1.5 text-lg font-semibold">
                  {bookingDetails?.checkin_date.split("T")[0]
                    .split("-")
                    .reverse()
                    .join("/")}{" "}
                  -{" "}
                  {bookingDetails?.checkout_date.split("T")[0]
                    .split("-")
                    .reverse()
                    .join("/")}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-2xl font-semibold">Detalhes da reserva</h3>
          <div className="flex flex-col space-y-4">
            <div className="flex text-neutral-6000 dark:text-neutral-300">
              <span className="flex-1">Código da reserva</span>
              <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
                {bookingDetails?.broker_reservation_id}
              </span>
            </div>
            <div className="flex text-neutral-6000 dark:text-neutral-300">
              <span className="flex-1">Data</span>
              <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
                {bookingDetails?.checkin_date.split("T")[0]
                  .split("-")
                  .reverse()
                  .join("/")}{" "}
                -{" "}
                {bookingDetails?.checkout_date.split("T")[0]
                  .split("-")
                  .reverse()
                  .join("/")}
              </span>
            </div>
          </div>
          <div className="flex text-neutral-6000 dark:text-neutral-300">
            <span className="flex-1">Nome do Hotel</span>
            <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
              {booking?.hotel_name}
            </span>
          </div>
          <div className="flex text-neutral-6000 dark:text-neutral-300">
            <span className="flex-1">Endereço do Hotel</span>
            <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
              {booking?.hotel_address}
            </span>
          </div>
          <div className="flex text-neutral-6000 dark:text-neutral-300">
            <span className="flex-1">Horários de check-in e check-out</span>
            <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
              Check-in {booking?.check_in_hour} - Check-out{" "}
              {booking?.check_out_hour}
            </span>
          </div>
        </div>
        <div className="flex text-neutral-6000 dark:text-neutral-300">
          <span className="mr-2">Link do voucher da sua reserva</span>
          {bookingDetails.voucher_url && (
            <a
              href={bookingDetails.voucher_url}
              target="_blank"
              className="font-medium text-blue dark:text-blue-light"
              rel="noreferrer"
            >
              Clique aqui
            </a>
          )}
        </div>
        {booking.cashback ? (
          <div className="bg-blue/20 border-dashed border-blue rounded-md p-4 border-2 mt-6">
            <p className="text-lg text-neutral-6000 dark:text-neutral-300">
              O CashBack de <span className="italic text-black dark:text-white font-semibold">{booking.cashback} ViroCash</span> será creditado em seu saldo, após a data de <span className="italic text-black dark:text-white font-semibold">{Formatter.prettyDateBRFormated(
                bookingDetails?.checkin_date)}</span>, considerando que a reserva não seja cancelada.
            </p>
          </div>
        ) : null}
        <div className="flex gap-6">
          <ButtonPrimary
            className="bg-[--blue] hover:bg-[--blue-dark]"
            href="/booking"
          >
            Minhas reservas
          </ButtonPrimary>
          <ButtonPrimary
            className="bg-[--green] hover:bg-[--green-dark]"
            href="/home"
          >
            Explorar mais hospedagens
          </ButtonPrimary>
        </div>
      </div>
    );
  };

  return (
    <MainTemplate>
      <div className={`nc-PayPage ${className}`} data-nc-id="PayPage">
        <main className="container mt-11 mb-24 lg:mb-32 ">
          <div className="max-w-4xl mx-auto">{renderContent()}</div>
        </main>
      </div>
    </MainTemplate>
  );
};
